import { StorageService } from '../../../../shared/services/storage.service';
import { VendorHandler } from '@portal/app/dashboard/integrations/shared/vendor-handlers/vendor-handler';
import {
  IOAuthDetails,
  IVendorConnectConfirmation,
  IVendorConnectInitRequest,
  IVendorConnectInitResponse
} from '@portal/app/dashboard/integrations/models/vendor/VendorIntegrationModels';
import { IVendorInitiateRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorInitiateRequest';
import {
  callbackDomain,
  VendorIdSource
} from '@portal/app/dashboard/integrations/shared/constants';
import { IVendorConfigurationRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorConfigurationRequest';
import { map } from 'lodash-es';
import { IVendorOAuthConfirmationRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorOAuthConfirmationRequest';
import { IVendorReConfigurationRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorReConfigurationRequest';
import { WindowService } from '@portal/app/shared/services/window.service';

export interface IAmazonRegionDefinition {
  authUrl: string;
  apiUrl: string;
  label: string;
}

export abstract class BaseAmazonHandler implements VendorHandler {
  constructor(
    protected storageService: StorageService,
    protected readonly windowService: WindowService
  ) {}

  protected abstract getVendorId(): keyof typeof VendorIdSource;
  protected abstract getRegionConfig(): Record<string, IAmazonRegionDefinition>;

  static buildAvailableRegions(
    config: Record<string, IAmazonRegionDefinition>
  ) {
    return Object.entries(config).map(([regionKey, entry]) => ({
      id: regionKey,
      label: entry.label,
      authUrl: entry.authUrl,
      apiUrl: entry.apiUrl
    }));
  }

  prepareRequestObjectToInitiateVendor(
    vendorDetails: IVendorConnectInitRequest
  ): IVendorInitiateRequest {
    const region = vendorDetails['region'] || 'NA';

    return {
      req: {},
      queryParams: {
        callbackDomain: callbackDomain(),
        region
      },
      pathParams: { vendorId: this.getVendorId() }
    };
  }

  handleVendorInitiateResponse(data: IVendorConnectInitResponse): void {
    const details = data.vendorCredentialsDetails as IOAuthDetails;
    const url = new URL(details.redirectURL);
    const params = new URLSearchParams(url.search);

    localStorage.setItem('appState', params.get('state') || '');
    localStorage.setItem(
      'callBackParams',
      JSON.stringify(details.callbackParameters)
    );
    this.windowService.navigateToHref(details.redirectURL);
  }

  prepareRequestToConfirmVendor(): IVendorOAuthConfirmationRequest {
    return {
      code: `${localStorage.getItem('spapi_oauth_code')}`,
      state: `${localStorage.getItem('state')}`,
      callbackDomain: callbackDomain()
    };
  }

  prepareRequestObjectToConfigureVendor(
    accounts: {
      accountName: string;
      accountId: string;
      clientAccountName?: string;
      managerId?: string;
      disabled: boolean;
      endpoint?: string;
    }[]
  ): IVendorConfigurationRequest {
    return {
      vendorId: this.getVendorId(),
      vendorData: map(accounts, (account) => ({
        accountId: account.accountId,
        accountName: account.accountName,
        endpoint: account.endpoint ? account.endpoint : ''
      }))
    };
  }

  confirmVendor(data: IVendorConnectConfirmation): void {
    const jsonData = data.accountDetails
      ? JSON.stringify(data.accountDetails.vendorAccounts)
      : '';
    this.storageService.setIntegrationAccountDetails(
      VendorIdSource[this.getVendorId()],
      jsonData
    );
  }

  prepareRequestObjectToReConfigureVendor(
    accounts: {
      accountName: string;
      accountId: string;
      managerId?: string | undefined;
      disabled: boolean;
      endpoint?: string;
      clientAccountName?: string;
    }[],
    dsInstanceIds: number[]
  ): IVendorReConfigurationRequest {
    return {
      vendorId: this.getVendorId(),
      vendorData: map(accounts, (account) => ({
        accountId: account.accountId,
        accountName: account.accountName,
        endpoint: account.endpoint ? account.endpoint : ''
      })),
      dsInstanceIds
    };
  }
}
